import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getBlogPost, createBlogPost, updateBlogPost } from '../../../services/api';

/**
 * Editor de post do blog.
 *
 * UX:
 *   - Título (gera slug automaticamente se o usuário não editar)
 *   - Textarea de Markdown com preview SIMPLES ao lado (client-side)
 *     O preview é só pra orientação visual; o HTML final é renderizado no servidor
 *     (marked + sanitize-html). Por isso não carregamos libs pesadas no frontend.
 *   - Campos SEO (meta description, tags, cover image + alt)
 *   - Status + data de publicação (agendamento)
 *
 * IMPORTANTE: o preview aqui usa regex básico e NÃO é fiel ao render final.
 * A única fonte de verdade é o HTML renderizado pelo servidor em /blog/:slug.
 */

// Preview de markdown minimalista — só pra dar uma noção visual rápida.
// Sem negrito/itálico complexo, sem tabelas, sem code blocks aninhados.
// O render real é no servidor (marked + sanitize-html).
function naiveMarkdownPreview(md) {
  if (!md) return '<p style="color:#94a3b8">(vazio — a prévia aparece aqui)</p>';
  let html = String(md);
  // Escape HTML básico pra não quebrar o preview
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Code blocks ```
  html = html.replace(/```([\s\S]*?)```/g, (_, code) => `<pre><code>${code}</code></pre>`);
  // Inline code `
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  // Bold
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  // Lists (muito básico — uma linha por vez)
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`);
  // Paragraphs — 2 newlines
  html = html.split(/\n{2,}/).map(chunk => {
    const trimmed = chunk.trim();
    if (!trimmed) return '';
    if (/^<(h1|h2|h3|ul|ol|pre|blockquote)/.test(trimmed)) return trimmed;
    return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
  }).join('\n');
  return html;
}

function slugify(str) {
  return String(str || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 180);
}

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  content_md: '',
  cover_image_url: '',
  cover_image_alt: '',
  meta_description: '',
  tags: '',
  author_name: 'Equipe AtendoApp',
  status: 'draft',
  published_at: '',
};

export default function MasterBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Carrega post em edição
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getBlogPost(id)
      .then(r => {
        const p = r.data.post;
        setForm({
          title: p.title || '',
          slug: p.slug || '',
          excerpt: p.excerpt || '',
          content_md: p.content_md || '',
          cover_image_url: p.cover_image_url || '',
          cover_image_alt: p.cover_image_alt || '',
          meta_description: p.meta_description || '',
          tags: p.tags || '',
          author_name: p.author_name || 'Equipe AtendoApp',
          status: p.status || 'draft',
          published_at: p.published_at ? new Date(p.published_at).toISOString().slice(0, 16) : '',
        });
        setSlugEdited(true); // em edição, nunca regerar slug automaticamente
      })
      .catch(() => {
        toast.error('Erro ao carregar post');
        navigate('/master/blog');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  // Gera slug automaticamente do título (só se o usuário não tocou manualmente)
  useEffect(() => {
    if (!slugEdited && form.title) {
      setForm(f => ({ ...f, slug: slugify(f.title) }));
    }
  }, [form.title, slugEdited]);

  const onChange = useCallback((field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  }, []);

  const onSlugChange = (val) => {
    setSlugEdited(true);
    onChange('slug', slugify(val));
  };

  const onSave = async (e, publishNow = false) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.length < 3) return toast.error('Título muito curto');
    if (!form.content_md.trim() || form.content_md.length < 20) return toast.error('Conteúdo muito curto');

    const payload = {
      ...form,
      published_at: form.published_at ? new Date(form.published_at).toISOString() : null,
    };
    if (publishNow) {
      payload.status = 'published';
      if (!payload.published_at) payload.published_at = new Date().toISOString();
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateBlogPost(id, payload);
        toast.success(publishNow ? 'Post publicado!' : 'Post atualizado');
        if (publishNow) setForm(f => ({ ...f, status: 'published' }));
      } else {
        const r = await createBlogPost(payload);
        toast.success(publishNow ? 'Post publicado!' : 'Post criado como rascunho');
        navigate(`/master/blog/${r.data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="spinner" style={{ margin: '60px auto', display: 'block' }} />;

  const tagList = (form.tags || '').split(',').map(t => t.trim()).filter(Boolean);

  return (
    <div className="page-body">
      <div className="page-header-row" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div className="page-header">
          <h2>{isEdit ? 'Editar post' : 'Novo post'}</h2>
          <p>{isEdit ? `Status atual: ${form.status === 'published' ? 'Publicado' : 'Rascunho'}` : 'Escreva o conteúdo em Markdown.'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/master/blog')}>
            ← Voltar
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(p => !p)}>
            {showPreview ? 'Ocultar prévia' : 'Ver prévia'}
          </button>
          {isEdit && form.status === 'published' && form.slug && (
            <a
              className="btn btn-secondary"
              href={`/blog/${encodeURIComponent(form.slug)}`}
              target="_blank" rel="noopener noreferrer"
            >Abrir no site →</a>
          )}
        </div>
      </div>

      <form onSubmit={e => onSave(e, false)}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr',
          gap: 20,
        }}>
          {/* Coluna 1: editor */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label>Título *</label>
              <input
                value={form.title}
                onChange={e => onChange('title', e.target.value)}
                placeholder="Ex: Como criar link de agendamento em 5 minutos"
                required
                maxLength={300}
              />
            </div>

            <div className="form-group">
              <label>Slug (URL) *</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-500)', marginBottom: 6 }}>
                <span>atendoapp.com/blog/</span>
                <strong style={{ color: 'var(--primary)' }}>{form.slug || '(vazio)'}</strong>
              </div>
              <input
                value={form.slug}
                onChange={e => onSlugChange(e.target.value)}
                placeholder="gerado do título automaticamente"
                maxLength={180}
              />
              <small>Só letras minúsculas, números e hífen. Idealmente 3-5 palavras-chave.</small>
            </div>

            <div className="form-group">
              <label>Resumo (excerpt)</label>
              <textarea
                value={form.excerpt}
                onChange={e => onChange('excerpt', e.target.value)}
                rows={2}
                placeholder="1-2 frases que aparecem na listagem e no Google (opcional — gerado do conteúdo se vazio)"
                maxLength={300}
              />
              <small>{form.excerpt.length}/300 caracteres</small>
            </div>

            <div className="form-group">
              <label>Conteúdo (Markdown) *</label>
              <textarea
                value={form.content_md}
                onChange={e => onChange('content_md', e.target.value)}
                rows={22}
                required
                placeholder="# Título principal

Escreva aqui em **Markdown**.

## Subtítulo

- Item de lista
- Outro item

[Link](https://atendoapp.com)"
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: 14,
                  lineHeight: 1.55,
                }}
              />
              <small>
                Suporta: # Headers, **negrito**, *itálico*, [link](url), ![img](url), - listas, &gt; citações, `código`, ```blocos```, tabelas
              </small>
            </div>

            <div className="form-group">
              <label>Imagem de capa (URL)</label>
              <input
                type="url"
                value={form.cover_image_url}
                onChange={e => onChange('cover_image_url', e.target.value)}
                placeholder="https://images.unsplash.com/photo-..."
              />
              <small>Cole URL do Unsplash/Pexels/seu CDN. Ideal 16:9 (1200×675).</small>
              {form.cover_image_url && (
                <img
                  src={form.cover_image_url}
                  alt="preview capa"
                  style={{ marginTop: 8, maxWidth: 320, borderRadius: 8, border: '1px solid var(--gray-200)' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
            </div>

            <div className="form-group">
              <label>Texto alternativo da capa (alt)</label>
              <input
                value={form.cover_image_alt}
                onChange={e => onChange('cover_image_alt', e.target.value)}
                placeholder="Descrição da imagem pra acessibilidade (opcional)"
                maxLength={300}
              />
            </div>

            <div className="form-group">
              <label>Meta description (SEO)</label>
              <textarea
                value={form.meta_description}
                onChange={e => onChange('meta_description', e.target.value)}
                rows={2}
                placeholder="Descrição curta que aparece no Google (opcional — fallback pro excerpt)"
                maxLength={300}
              />
              <small>{form.meta_description.length}/300 caracteres · ideal: 150-160</small>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <input
                value={form.tags}
                onChange={e => onChange('tags', e.target.value)}
                placeholder="agendamento, salao, pix"
              />
              <small>Separadas por vírgula. Usadas no filtro e nos posts relacionados.</small>
              {tagList.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {tagList.map(t => (
                    <span key={t} style={{
                      fontSize: 11, background: 'var(--primary-light)', color: 'var(--primary)',
                      padding: '3px 10px', borderRadius: 999, fontWeight: 600,
                    }}>{t.toLowerCase()}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Autor</label>
                <input
                  value={form.author_name}
                  onChange={e => onChange('author_name', e.target.value)}
                  placeholder="Equipe AtendoApp"
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => onChange('status', e.target.value)}>
                  <option value="draft">Rascunho</option>
                  <option value="published">Publicado</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Data de publicação</label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={e => onChange('published_at', e.target.value)}
              />
              <small>
                Futuro = agendar · Vazio + status=publicado = publica agora ao salvar
              </small>
            </div>
          </div>

          {/* Coluna 2: preview */}
          {showPreview && (
            <div style={{
              background: 'var(--gray-50)',
              border: '1px solid var(--gray-200)',
              borderRadius: 10,
              padding: 20,
              position: 'sticky',
              top: 20,
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 80px)',
              overflowY: 'auto',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 12 }}>
                Prévia (aproximada)
              </div>
              <h1 style={{ fontSize: 24, marginTop: 0, color: 'var(--gray-900)' }}>{form.title || '(sem título)'}</h1>
              {form.excerpt && (
                <p style={{ fontSize: 15, color: 'var(--gray-600)', marginBottom: 20 }}>{form.excerpt}</p>
              )}
              {form.cover_image_url && (
                <img src={form.cover_image_url} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 16 }} onError={e => { e.target.style.display = 'none'; }} />
              )}
              <div
                style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--gray-800)' }}
                dangerouslySetInnerHTML={{ __html: naiveMarkdownPreview(form.content_md) }}
              />
              <div style={{ marginTop: 24, padding: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                ⚠️ Esta é uma prévia aproximada (sem tabelas, sem GFM completo).
                O layout real só aparece ao salvar e acessar <code>/blog/{form.slug || 'seu-slug'}</code>.
              </div>
            </div>
          )}
        </div>

        <div style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--white)',
          borderTop: '1px solid var(--gray-200)',
          padding: '14px 0',
          marginTop: 24,
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/master/blog')} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className={`btn btn-secondary${saving ? ' btn-loading' : ''}`} disabled={saving}>
            {saving ? 'Salvando...' : (isEdit ? 'Salvar alterações' : 'Salvar como rascunho')}
          </button>
          {form.status !== 'published' && (
            <button type="button" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} onClick={e => onSave(e, true)} disabled={saving}>
              {saving ? 'Publicando...' : 'Publicar agora'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
